import {
  describeErrorForLog,
  describeUrlForLog,
} from '@/utils/logger/describe-url-for-log';
import { logger } from '@/utils/logger/logger';

/**
 * Marks an anchor whose own click handler is responsible for opening it, so
 * the global handler leaves it alone. See the note in the setup doc block.
 */
export const SELF_OPENING_LINK_ATTRIBUTE = 'data-opens-itself';

/**
 * The installed handler's disposer. A second setup replaces the first rather
 * than stacking on it: the second would otherwise capture the already-patched
 * `window.open` as its original and wrap the patch in itself, so one call would
 * reach the OS twice. Production installs once at module scope, but `_app.tsx`
 * re-evaluates on every dev Fast Refresh.
 */
let activeDisposer: (() => void) | undefined;

/**
 * Schemes the OS owns rather than the webview. Every one of these is handed to
 * `openUrl`, so the capability's scope has to grant it or the call is rejected
 * at runtime with the suite still green — exported so the test can pin this
 * list against the granted globs rather than restating them.
 */
export const EXTERNAL_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Hosts Tauri serves from inside the app on Windows, where its custom
 * protocols surface as `http://<name>.localhost` (`convertFileSrc` gives
 * `http://asset.localhost/...`). They are off-origin to the page but the OS
 * browser cannot load them, so they stay in the webview like same-origin.
 */
const TAURI_INTERNAL_HOSTS = ['asset.localhost', 'ipc.localhost'];

/**
 * The url to hand to the OS, or null when the webview should keep the click.
 * Same-origin http(s) is in-app navigation; `mailto:`/`tel:` never are.
 */
function resolveExternalUrl(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }

  if (!EXTERNAL_SCHEMES.includes(url.protocol)) return null;
  if (
    (url.protocol === 'http:' || url.protocol === 'https:') &&
    (url.origin === window.location.origin || TAURI_INTERNAL_HOSTS.includes(url.hostname))
  ) {
    return null;
  }

  return url.href;
}

/**
 * Sets up external link handling for Tauri desktop app.
 *
 * This owns "a click on an external anchor should open the OS browser". The
 * opener plugin injects its own listener as a native backstop — it also covers
 * the gap before this one is installed — and that listener bails on
 * `defaultPrevented`, so this handler wins every click it claims. Two
 * carve-outs the plugin keeps: Ctrl/Shift+click on an anchor this handler
 * leaves alone (same-origin or `data-opens-itself`), and anything clicked
 * before app bootstrap finishes.
 *
 * It deliberately does not call `stopPropagation()`. The anchor's own click
 * handler is often that CTA's analytics, and stopping propagation at
 * `document` in the capture phase drops it before React ever sees it.
 *
 * Because it claims the click in the capture phase, an anchor's own
 * `preventDefault()` can no longer veto the hand-off — by the time the anchor
 * runs, the url is already on its way to the OS. An anchor that means "do not
 * navigate, do X instead" must therefore say so with `data-opens-itself`, or
 * the user gets both.
 *
 * It also overrides `window.open`, which third-party widgets like Intercom
 * call directly and Tauri does not forward to the system browser.
 *
 * @returns a disposer that detaches the listener and restores `window.open`.
 */
export async function setupExternalLinkHandler(): Promise<() => void> {
  logger.info('[external-link-handler] Setting up external link handler for Tauri');

  // Dynamic import to avoid loading Tauri plugin on web
  const { openUrl } = await import('@tauri-apps/plugin-opener');

  // After the await, not before it: two overlapping calls would otherwise both
  // find no disposer, both install, and the second would capture the first's
  // patched window.open as its original — two opens per click. Fast Refresh
  // re-evaluates `_app.tsx` in dev.
  activeDisposer?.();

  const handOffToOs = (url: string) => {
    void openUrl(url).catch((err: unknown) => {
      logger.error('[external-link-handler] Failed to open URL in system browser', {
        url: describeUrlForLog(url),
        error: describeErrorForLog(err),
      });
    });
  };

  // The reference, not a bound copy, so disposing restores window.open to the
  // exact function it was — otherwise a repeat setup/dispose cycle leaves a
  // stack of bound wrappers behind.
  const originalWindowOpen = window.open;

  // Override window.open to intercept external URLs
  window.open = function (
    url?: string | URL,
    target?: string,
    features?: string
  ): WindowProxy | null {
    if (url) {
      const external = resolveExternalUrl(url.toString());

      if (external) {
        logger.debug('[external-link-handler] Intercepted window.open for external URL', {
          url: describeUrlForLog(external),
        });
        handOffToOs(external);
        return null;
      }
    }

    // Fall back to original behavior for same-origin or non-external URLs
    return originalWindowOpen.call(window, url, target, features);
  };

  const onClickCapture = (e: MouseEvent) => {
    if (e.defaultPrevented) return;

    const anchor = e
      .composedPath()
      .find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement);

    if (!anchor?.href) return;

    // This anchor's own handler opens the url, or opens something else in its
    // place. Claiming it here would do both. Returning without preventing
    // leaves the anchor's own `preventDefault()` in charge.
    if (anchor.hasAttribute(SELF_OPENING_LINK_ATTRIBUTE)) return;

    const external = resolveExternalUrl(anchor.href);
    if (!external) return;

    // Ignore `target`. This window has no browser chrome, so every
    // off-origin click goes to the OS. Same-origin routes never reach here.

    // No stopPropagation — see the note above. The anchor's own handler
    // still runs, and the opener plugin's listener skips prevented events.
    e.preventDefault();

    logger.debug('[external-link-handler] Intercepted anchor click for external URL', {
      url: describeUrlForLog(external),
    });

    handOffToOs(external);
  };

  // Capture phase, so the click is claimed before the webview navigates.
  document.addEventListener('click', onClickCapture, true);

  logger.info('[external-link-handler] External link handler setup complete');

  // A superseded disposer is inert: calling it would otherwise tear down
  // whichever setup replaced it, and restore a window.open two patches stale.
  const dispose = () => {
    if (activeDisposer !== dispose) return;
    document.removeEventListener('click', onClickCapture, true);
    window.open = originalWindowOpen;
    activeDisposer = undefined;
  };

  activeDisposer = dispose;
  return dispose;
}
